import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@mono/ui-components';
import { Plus } from 'lucide-react';
import { memo, useCallback, useMemo, useState } from 'react';
import { useCLINs } from '../../../hooks/useCLINs';
import { exportCLINs } from '../../../lib/utils/exportToExcel';
import { ExportButton } from '../ComplianceMatrix/ExportButton';
import { groupByLogicalKey } from '../v2/amendment/groupByLogicalKey';
import { HistoryToggle } from '../v2/amendment/HistoryToggle';
import { CLINDetailDrawer } from './CLINDetailDrawer';
import { CLINsTable } from './CLINsTable';
export const CLINsTab = /*#__PURE__*/ memo(function CLINsTab({ projectId }) {
    const { data, isLoading, error } = useCLINs(projectId);
    // Store the stable identifier rather than the ICLIN snapshot — same
    // pattern as LCATsTab — so the drawer rerenders with fresh data after
    // a save invalidates useCLINs instead of pinning to the stale object
    // captured at click time.
    const [selectedClinId, setSelectedClinId] = useState(null);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const allClins = data?.clins || [];
    const referenceContent = data?.referenceContent;
    // PR-C.5: amendment-aware view defaults to current-only. Same shape as
    // PR-C.4 (LCATs) — group by logicalKey, render currentList by default,
    // expand to full history on toggle. Each per-doc CLIN row already
    // carries `logicalKey + isCurrentVersion` from the merge step in PR-A.
    const [showHistory, setShowHistory] = useState(false);
    const grouped = useMemo(()=>groupByLogicalKey(allClins), [
        allClins
    ]);
    const clins = useMemo(()=>{
        if (showHistory) {
            const out = [];
            for (const key of grouped.allKeys){
                const versions = grouped.historyByKey.get(key) ?? [];
                out.push(...versions);
            }
            return out;
        }
        return grouped.currentList;
    }, [
        grouped,
        showHistory
    ]);
    const handleExport = useCallback((format)=>exportCLINs(clins, format), [
        clins
    ]);
    // Pick the matching CLIN out of the live list every render — the
    // useCLINs cache invalidation on save flows here automatically.
    const selectedCLIN = useMemo(()=>{
        if (!selectedClinId) return null;
        return allClins.find((c)=>(c.clinId ?? c.clinNumber) === selectedClinId) ?? null;
    }, [
        allClins,
        selectedClinId
    ]);
    const handleRowClick = (clin)=>{
        setSelectedClinId(clin.clinId ?? clin.clinNumber);
        setIsDetailOpen(true);
    };
    const handleCloseDetail = ()=>{
        setIsDetailOpen(false);
        setSelectedClinId(null);
    };
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex items-center justify-center h-64",
            children: /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col items-center gap-3",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-gray-500",
                        children: "Loading..."
                    })
                ]
            })
        });
    }
    if (error) {
        return /*#__PURE__*/ _jsx("div", {
            className: "py-12 text-center",
            children: /*#__PURE__*/ _jsxs("div", {
                className: "max-w-md mx-auto space-y-4",
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "text-lg font-semibold text-red-600",
                        children: "Error Loading CLINs"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-gray-600",
                        children: error && typeof error === 'object' && 'message' in error ? String(error.message) : 'Failed to load CLINs'
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-gray-500",
                        children: "Please check the browser console for more details or contact support."
                    })
                ]
            })
        });
    }
    if (clins.length === 0) {
        return /*#__PURE__*/ _jsx("div", {
            className: "py-12 text-center",
            children: /*#__PURE__*/ _jsxs("div", {
                className: "max-w-md mx-auto space-y-4",
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "text-lg font-semibold text-gray-900",
                        children: "No CLINs Found"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-gray-600",
                        children: "No Contract Line Item Numbers have been extracted from your solicitation documents yet."
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-gray-500",
                        children: "Upload solicitation documents to automatically extract CLINs."
                    })
                ]
            })
        });
    }
    // PR-B.7.3: stats panel reads from grouped.currentList instead of the
    // raw clins.length / stats.totalCLINs from useCLINs. Fixes the
    // user-reported "CLINs total count not showing unique count" issue —
    // before this change the panel showed 780 (every per-amendment row)
    // when only 195 are canonical. Sum currentList values for the four
    // tile stats instead of trusting the now-stale backend stats blob.
    const currentClins = grouped.currentList;
    const baseCLINs = currentClins.filter((c)=>!c.optionType || c.optionType === 'base').length;
    const optionCLINs = currentClins.filter((c)=>c.optionType && c.optionType !== 'base').length;
    // Pricing reduce: prefer the parsed numeric value (set by extractor when
    // it can structure-parse the price string). Fall back to a Number()
    // parse on the raw string when only the unparsed value is present —
    // covers test fixtures and legacy data alike.
    const totalValue = currentClins.reduce((sum, c)=>{
        const parsed = c.pricing?.extendedPriceParsed?.numericValue;
        if (typeof parsed === 'number') return sum + parsed;
        const raw = c.pricing?.extendedPrice;
        if (raw) {
            const n = Number(String(raw).replace(/[^0-9.-]/g, ''));
            if (!Number.isNaN(n)) return sum + n;
        }
        return sum;
    }, 0);
    const showAmendmentBreakdown = grouped.totalRows > grouped.currentList.length;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-6",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-1 md:grid-cols-4 gap-4",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "bg-white border border-gray-200 rounded-lg p-4",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm text-gray-500",
                                children: showAmendmentBreakdown ? 'Current CLINs' : 'Total CLINs'
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-2xl font-semibold mt-1",
                                children: currentClins.length.toLocaleString()
                            }),
                            showAmendmentBreakdown && /*#__PURE__*/ _jsxs("div", {
                                className: "mt-0.5 text-[11px] text-gray-400",
                                children: [
                                    "of ",
                                    grouped.totalRows.toLocaleString(),
                                    " across all amendments"
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "bg-white border border-gray-200 rounded-lg p-4",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm text-gray-500",
                                children: "Base CLINs"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-2xl font-semibold mt-1",
                                children: baseCLINs.toLocaleString()
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "bg-white border border-gray-200 rounded-lg p-4",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm text-gray-500",
                                children: "Option CLINs"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-2xl font-semibold mt-1",
                                children: optionCLINs.toLocaleString()
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "bg-white border border-gray-200 rounded-lg p-4",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm text-gray-500",
                                children: "Total Value"
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "text-2xl font-semibold mt-1",
                                children: [
                                    "$",
                                    totalValue.toLocaleString()
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "bg-white border border-gray-200 rounded-lg",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-3 flex-wrap",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("h2", {
                                        className: "text-lg font-semibold",
                                        children: "Contract Line Items"
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm text-gray-500 mt-1",
                                        children: grouped.currentList.length === grouped.totalRows ? `${grouped.currentList.length} CLINs extracted from solicitation` : `${grouped.currentList.length} current CLINs (of ${grouped.totalRows.toLocaleString()} across all amendments)`
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2 flex-wrap",
                                children: [
                                    /*#__PURE__*/ _jsx(HistoryToggle, {
                                        currentCount: grouped.currentList.length,
                                        totalCount: grouped.totalRows,
                                        showHistory: showHistory,
                                        onToggle: setShowHistory,
                                        noun: "CLIN"
                                    }),
                                    /*#__PURE__*/ _jsx(ExportButton, {
                                        onExport: handleExport,
                                        disabled: clins.length === 0
                                    }),
                                    /*#__PURE__*/ _jsxs(Button, {
                                        variant: "outline",
                                        size: "sm",
                                        disabled: true,
                                        children: [
                                            /*#__PURE__*/ _jsx(Plus, {
                                                className: "w-4 h-4 mr-2"
                                            }),
                                            "Add CLIN"
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(CLINsTable, {
                        clins: clins,
                        onRowClick: handleRowClick
                    })
                ]
            }),
            selectedCLIN && /*#__PURE__*/ _jsx(CLINDetailDrawer, {
                clin: selectedCLIN,
                projectId: projectId,
                isOpen: isDetailOpen,
                onClose: handleCloseDetail,
                referenceContent: referenceContent
            })
        ]
    });
});
